import api from './api';

export type PermissionAction = 'read' | 'create' | 'update' | 'delete' | 'approve' | 'export' | 'execute';

// One area a role can be allowed into, such as Sales, with the actions that exist there
export interface PermissionArea {
  key: string;
  label: string;
  description: string;
  group: string;
  actions: PermissionAction[];
  // What an action means here, when the plain word is not enough
  actionHints?: Partial<Record<PermissionAction, string>>;
  // Other permissions the pages of this area cannot work without
  needs?: string[];
}

export interface PermissionCatalogue {
  actions: Record<PermissionAction, string>;
  areas: PermissionArea[];
  total: number;
}

export interface Role {
  id: string;
  // The fixed key, such as "store_keeper"
  name: string;
  displayName: string;
  description: string | null;
  isActive: boolean;
  // Built-in roles cannot be deleted
  isSystem: boolean;
  // The role that always holds everything
  isAdministrator: boolean;
  priority: number;
  // "<area>.<action>" codes
  permissions: string[];
  userCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface RoleDetail extends Role {
  users: { id: string; name: string; email: string; status: string }[];
}

export interface RoleSummary {
  total: number;
  active: number;
  custom: number;
  // Active users whose role is switched off: they cannot sign in
  usersWithoutAccess: number;
  permissionCount: number;
}

export interface CreateRoleRequest {
  displayName: string;
  description?: string | null;
  permissions: string[];
  priority?: number;
  isActive?: boolean;
}

export type UpdateRoleRequest = Partial<CreateRoleRequest>;

export class RoleService {
  static async getAllRoles(): Promise<Role[]> {
    const response = await api.get('/roles');
    return response.data;
  }

  static async getCatalogue(): Promise<PermissionCatalogue> {
    const response = await api.get('/roles/catalogue');
    return response.data;
  }

  static async getSummary(): Promise<RoleSummary> {
    const response = await api.get('/roles/summary');
    return response.data;
  }

  static async getRoleById(id: string): Promise<RoleDetail> {
    const response = await api.get(`/roles/${id}`);
    return response.data;
  }

  static async createRole(roleData: CreateRoleRequest): Promise<Role> {
    const response = await api.post('/roles', roleData);
    return response.data;
  }

  static async updateRole(id: string, roleData: UpdateRoleRequest): Promise<Role> {
    const response = await api.patch(`/roles/${id}`, roleData);
    return response.data;
  }

  // A copy of a role under a new name
  static async cloneRole(id: string, displayName: string): Promise<Role> {
    const response = await api.post(`/roles/${id}/clone`, { displayName });
    return response.data;
  }

  static async deleteRole(id: string): Promise<void> {
    await api.delete(`/roles/${id}`);
  }
}

export default RoleService;
